import {
    Component, OnInit, OnDestroy,
    ViewChild, ElementRef, signal, WritableSignal, inject
} from '@angular/core';
import {
    AbstractControl, FormBuilder, FormControl, FormGroup, FormGroupDirective, ValidationErrors, Validators
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import { ErrorStateMatcher, MatOptionSelectionChange } from '@angular/material/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';

import { MatterService } from '../shared/services/matter.service';
import { RequestsService } from '../shared/services/requests.service';
import { SnackbarService } from '../shared/services/snackbar.service';
import { CommonService } from '../shared/services/common.service';
import { BankAccountService } from '../shared/services/bank-account.service';
import { ManageUserRoleService } from '../shared/services/manage-user-role.service';
import { UserService } from '../shared/services/user.service';

import { Matter } from '../shared/models/matter';
import { AddPaymentRequestDto } from '../shared/models/paymentRequestDto';
import { AttachmentData, RequestDocAttachmentResponse } from '../shared/models/requestDocAttachment';
import { Authoriser } from '../shared/models/authoriser';
import { Currency } from '../shared/models/currency';
import { BankAccount } from '../shared/models/bankAccount';
import { PurchaseCodeDto } from '../shared/models/PurchaseCodeLookup';
import { RequestType } from '../enums/request-type.enum';
import { DraftEditor, DraftSaveOutcome } from '../shared/draft-editor/draft-editor';
import { DraftSnapshot, readBoolean, readDate, readNumber, readString } from '../shared/draft-editor/draft-snapshot';

/** Domestic payment method values */
const PAYMENT_METHOD_CHAPS = 'CHAPS';
const PAYMENT_METHOD_FASTER = 'Faster Payment';

@Component({
    selector: 'app-payment-request',
    templateUrl: './payment-request.component.html',
    styleUrls: ['./payment-request.component.scss'],
    providers: [DraftEditor]
})
export class PaymentRequestComponent implements OnInit, OnDestroy {

    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

    /** Saves this form as a draft and reopens saved drafts. */
    readonly draftEditor = inject(DraftEditor);
    private readonly route = inject(ActivatedRoute);

    /** A payment needs at least one document: either chosen on this form, or already saved on the draft. */
    private readonly atLeastOneDocument = (control: AbstractControl): ValidationErrors | null =>
        (Array.isArray(control.value) && control.value.length > 0) || this.draftEditor.documents().length > 0
            ? null
            : { required: true };

    // ── Public state ──────────────────────────────────────────────────────
    currentDate = new Date();
    paymentRequestForm!: FormGroup;

    /** True = International, False = Domestic */
    isInternational = false;
    /** True = Office payment, False = Client payment */
    isOfficePayment = false;

    isUserPartner: boolean | null = null;
    submitBtn = false;
    isLoading = false;
    isPayeeReadOnly = false;
    noData = false;

    matterDropdownData: Matter[] = [];
    clientOptions: string[] = [];
    readonly payeeClients = new FormControl<string[]>([], { nonNullable: true });
    readonly payeeErrorMatcher: ErrorStateMatcher = {
        isErrorState: () => {
            const payee = this.paymentRequestForm?.get('payee');
            return !!payee && payee.touched && payee.invalid;
        }
    };
    currencyDropdown: Currency[] = [];
    partnerList: Authoriser[] = [];
    bankAccounts: BankAccount[] = [];
    filteredBankAccounts: BankAccount[] = [];
    purposeCodeDropdown: PurchaseCodeDto[] = [];

    readonly paymentMethodDropdown = [PAYMENT_METHOD_CHAPS, PAYMENT_METHOD_FASTER];

    displayFile: string[] = [];
    fileAttachments: AttachmentData[] = [];

    matterNo = '';
    matterDesc = '';

    showPropertyPurchaseCheckbox = false;
    showPurposeCodeDropdown = false;

    actionstepMatterUrl = '';
    actionstepMatterOverviewUrl = '';
    showLinkToOpenMatter: WritableSignal<boolean> = signal(false);

    private readonly destroy$ = new Subject<void>();
    /** True while a saved draft is being put back into the form, so answers that look values up do not overwrite saved ones. */
    private restoringDraft = false;

    // ── Private base URLs ──────────────────────────────────────────────────
    private actionstepBaseUrl = '';
    private actionstepOverviewBaseUrl = '';

    constructor(
        private fb: FormBuilder,
        private matterService: MatterService,
        private requestService: RequestsService,
        private snackbarService: SnackbarService,
        private commonService: CommonService,
        private bankAccountService: BankAccountService,
        private manageUserRoleService: ManageUserRoleService,
        private userService: UserService
    ) { }

    // ── Lifecycle ──────────────────────────────────────────────────────────

    async ngOnInit(): Promise<void> {
        this.draftEditor.configure(RequestType.Payment);
        this.buildForm();
        this.draftEditor.watchForm(() => ({ snapshot: this.buildDraftSnapshot(), pendingFiles: this.fileAttachments.length }));
        this.setupFormListeners();
        this.updateAllDynamicValidators();
        await this.loadInitialData();
        // Before the matter search is wired up, so restoring the matter does not trigger a search.
        await this.restoreDraftFromRoute();
        this.setupMatterAutocomplete();
        this.draftEditor.markClean();
    }

    /** Whether the form holds changes that have not been saved. Used to warn the user before they leave. */
    hasUnsavedChanges(): boolean {
        return this.draftEditor.hasUnsavedChanges();
    }

    // ── Drafts ─────────────────────────────────────────────────────────────

    /**
     * True when there is something worth saving as a draft: the form differs from how it started (or has files waiting),
     * or this is a saved draft. 
     */
    get canSaveDraft(): boolean {
        return this.draftEditor.hasUnsavedChanges() || this.draftEditor.draftId() !== null;
    }

    /**
     * Saves the form as a draft, however incomplete it is.
     * @returns True only when the draft and all its new documents were saved.
     */
    async saveDraft(): Promise<boolean> {
        const f = this.paymentRequestForm.getRawValue();
        const amount = Number(String(f.amount ?? '').replace(/,/g, ''));

        const outcome = await this.draftEditor.save({
            snapshot: this.buildDraftSnapshot(),
            summary: {
                matterNo: Number(this.matterNo) || null,
                matter: this.matterDesc || null,
                clientName: f.clientName || null,
                amount: f.amount && Number.isFinite(amount) ? amount : null,
                reason: f.reason || null
            },
            newFiles: [...this.fileAttachments]
        });

        if (outcome === DraftSaveOutcome.Saved) {
            this.clearUpload();
        }
        // Documents now saved on the draft count towards "at least one file".
        this.paymentRequestForm.get('uploadedFiles')?.updateValueAndValidity();
        return outcome === DraftSaveOutcome.Saved;
    }

    /**
     * Removes a document that is already saved on the draft.
     * @param documentId The document's id.
     */
    async removeDraftDocument(documentId: number): Promise<void> {
        await this.draftEditor.removeDocument(documentId);
        const files = this.paymentRequestForm.get('uploadedFiles');
        files?.updateValueAndValidity();
        files?.markAsTouched();
    }

    /**
     * Handles the Save as Draft button: saves the draft and, once everything is saved, clears the form for the next request,
     * as after a submit. If anything could not be saved, the form stays as it is.
     * @param formDirective The form's directive, so the form is reset together with its submitted state.
     */
    async saveDraftAndReset(formDirective: FormGroupDirective): Promise<void> {
        if (!await this.saveDraft()) return;

        this.resetForm(formDirective);
        await this.draftEditor.afterDraftSaved();
    }

    private async restoreDraftFromRoute(): Promise<void> {
        const draftId = Number(this.route.snapshot.paramMap.get('draftId'));
        if (!Number.isInteger(draftId) || draftId <= 0) return;

        const snapshot = await this.draftEditor.open(draftId);
        if (snapshot) {
            await this.applyDraftSnapshot(snapshot);
        }
    }

    private buildDraftSnapshot(): DraftSnapshot {
        const f = this.paymentRequestForm.getRawValue();
        const bankAccount = f.clientBankAccount as BankAccount | '' | null;

        return {
            values: {
                matter: typeof f.matter === 'string' ? f.matter : '',
                clientName: f.clientName ?? '',
                isMoniesReturned: f.isMoniesReturned === true,
                returnMoniesExplanationIfNo: f.returnMoniesExplanationIfNo ?? '',
                reason: f.reason ?? '',
                payee: f.payee ?? '',
                amount: f.amount ?? '',
                vatAmount: f.vatAmount ?? '',
                clientBankAccountId: bankAccount ? bankAccount.accountId : '',
                chapsRequired: f.chapsRequired ?? '',
                isPropertyPurchase: f.isPropertyPurchase === true,
                purposeCode: f.purposeCode ?? null,
                sortCode: (f.sortCode ?? '').replace(/\D/g, ''),
                accountNumber: f.accountNumber ?? '',
                bankName: f.bankName ?? '',
                payeeReference: f.payeeReference ?? '',
                currency: f.currency ?? '',
                intermediaryBankName: f.intermediaryBankName ?? '',
                intermediaryAccountNumber: f.intermediaryAccountNumber ?? '',
                intermediarySwiftBicCode: f.intermediarySwiftBicCode ?? '',
                intermediaryReference: f.intermediaryReference ?? '',
                beneficiaryAccountNumber: f.beneficiaryAccountNumber ?? '',
                swiftBicCode: f.swiftBicCode ?? '',
                finalReference: f.finalReference ?? '',
                otherInformation: f.otherInformation ?? '',
                address: f.address ?? '',
                isAccountVerified: f.isAccountVerified ?? '',
                telephoneVerificationDate: f.telephoneVerificationDate instanceof Date ? f.telephoneVerificationDate.toISOString() : null,
                verificationSpokeTo: f.verificationSpokeTo ?? '',
                verificationNoReason: f.verificationNoReason ?? '',
                partnerAuthorisingId: f.partnerAuthorisingId ?? '',
                matterPartner: f.matterPartner === true,
                nonPartnerReason: f.nonPartnerReason ?? '',
                fundsCheckedByPartner: f.fundsCheckedByPartner === true,
                isPartnerCertified: f.isPartnerCertified === true
            },
            state: {
                matterNo: this.matterNo,
                matterDesc: this.matterDesc,
                isInternational: this.isInternational,
                isOfficePayment: this.isOfficePayment
            }
        };
    }

    private async applyDraftSnapshot(snapshot: DraftSnapshot): Promise<void> {
        const { values, state } = snapshot;
        const form = this.paymentRequestForm;

        this.matterNo = readString(state, 'matterNo');
        this.matterDesc = readString(state, 'matterDesc');
        if (this.matterNo) {
            this.actionstepMatterUrl = `${this.actionstepBaseUrl}?action_id=${this.matterNo}`;
            this.actionstepMatterOverviewUrl = `${this.actionstepOverviewBaseUrl}/${this.matterNo}`;
            this.showLinkToOpenMatter.set(true);
        }

        this.isInternational = readBoolean(state, 'isInternational');
        this.isOfficePayment = readBoolean(state, 'isOfficePayment');
        if (this.isInternational && !this.currencyDropdown.length) {
            try {
                this.currencyDropdown = await this.requestService.getAllCurrencies();
            } catch {
                this.snackbarService.errorSnackBar('Failed to load currencies');
            }
        }

        const chapsRequired = readString(values, 'chapsRequired');
        const isPropertyPurchase = readBoolean(values, 'isPropertyPurchase');
        this.showPropertyPurchaseCheckbox = !this.isInternational && chapsRequired === PAYMENT_METHOD_CHAPS;
        this.showPurposeCodeDropdown = this.showPropertyPurchaseCheckbox && isPropertyPurchase;

        // 1. The matter (choosing one enables the payee and "monies returned" fields), then, quietly, the answers
        //    that decide which other fields apply. Quietly, because reacting to each of them would wipe fields.
        form.patchValue({ matter: readString(values, 'matter'), matterNumber: this.matterNo });
        form.patchValue({
            chapsRequired,
            isPropertyPurchase,
            intermediaryBankName: readString(values, 'intermediaryBankName'),
            isMoniesReturned: readBoolean(values, 'isMoniesReturned'),
            isAccountVerified: readString(values, 'isAccountVerified'),
            matterPartner: readBoolean(values, 'matterPartner')
        }, { emitEvent: false });

        // 2. Work out which fields are required for those answers. This also clears fields that do not apply...
        //    While restoring, "monies returned = Yes" must not look the payee up: the saved payee is put back in step 3.
        this.restoringDraft = true;
        try {
            this.updateAllDynamicValidators();
        } finally {
            this.restoringDraft = false;
        }

        // 3. ...so every saved value goes back in afterwards.
        const bankAccountId = readString(values, 'clientBankAccountId');
        const purposeCode = readNumber(values, 'purposeCode');
        const partnerId = readNumber(values, 'partnerAuthorisingId');
        const currency = readString(values, 'currency');
        form.patchValue({
            clientName: readString(values, 'clientName'),
            returnMoniesExplanationIfNo: readString(values, 'returnMoniesExplanationIfNo'),
            reason: readString(values, 'reason'),
            payee: readString(values, 'payee'),
            amount: readString(values, 'amount'),
            vatAmount: readString(values, 'vatAmount'),
            clientBankAccount: this.bankAccounts.find(account => account.accountId === bankAccountId) ?? '',
            purposeCode: this.purposeCodeDropdown.some(code => code.id === purposeCode) ? purposeCode : null,
            sortCode: readString(values, 'sortCode'),
            accountNumber: readString(values, 'accountNumber'),
            bankName: readString(values, 'bankName'),
            payeeReference: readString(values, 'payeeReference'),
            currency: this.currencyDropdown.some(item => item.currencyCode === currency) ? currency : '',
            intermediaryAccountNumber: readString(values, 'intermediaryAccountNumber'),
            intermediarySwiftBicCode: readString(values, 'intermediarySwiftBicCode'),
            intermediaryReference: readString(values, 'intermediaryReference'),
            beneficiaryAccountNumber: readString(values, 'beneficiaryAccountNumber'),
            swiftBicCode: readString(values, 'swiftBicCode'),
            finalReference: readString(values, 'finalReference'),
            otherInformation: readString(values, 'otherInformation'),
            address: readString(values, 'address'),
            telephoneVerificationDate: readDate(values, 'telephoneVerificationDate'),
            verificationSpokeTo: readString(values, 'verificationSpokeTo'),
            verificationNoReason: readString(values, 'verificationNoReason'),
            // A saved partner who has since lost that role is no longer in the list; the user must choose again.
            partnerAuthorisingId: this.partnerList.some(partner => partner.id === partnerId) ? partnerId : '',
            nonPartnerReason: readString(values, 'nonPartnerReason'),
            fundsCheckedByPartner: readBoolean(values, 'fundsCheckedByPartner'),
            isPartnerCertified: readBoolean(values, 'isPartnerCertified')
        }, { emitEvent: false });

        if (this.matterNo) {
            await this.loadClientDetails(Number(this.matterNo));
            const savedPayee = `, ${readString(values, 'payee')}, `;
            this.payeeClients.setValue(this.clientOptions.filter(name => savedPayee.includes(`, ${name}, `)));
        }

        // A draft saved before the payee lookup finished has no payee. Look it up now, and wait for it, so the form
        // is complete before it is recorded as clean.
        if (readBoolean(values, 'isMoniesReturned') && !form.get('payee')?.value) {
            await this.loadClientNameIntoPayee();
        }

        // Documents already saved on the draft count towards the required "at least one file".
        form.get('uploadedFiles')?.updateValueAndValidity({ emitEvent: false });

        // Submit stays disabled until the form counts as touched. Touch the form itself, not every field,
        // so the user is not shown an error on everything they have not filled in yet.
        form.markAsTouched();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ── Form construction ──────────────────────────────────────────────────

    private buildForm(): void {
        this.paymentRequestForm = this.fb.group({
            // Core identity
            matter: ['', Validators.required],
            matterNumber: [''],
            clientName: [''],

            // Dates
            dateRequested: [new Date(), Validators.required],
            requestedTime: [this.toTimeString(new Date()), Validators.required],

            // Monies / reason
            isMoniesReturned: [false, Validators.required],
            returnMoniesExplanationIfNo: [''],
            reason: ['', Validators.required],

            // Payee — named payee to match DTO field
            payee: ['', Validators.required],
            amount: ['', [Validators.required, Validators.pattern(/^[0-9.,]*$/)]],
            vatAmount: [''],
            clientBankAccount: ['', Validators.required],

            // Domestic banking
            chapsRequired: [''],
            isPropertyPurchase: [false],
            purposeCode: [null],
            sortCode: [''],
            accountNumber: [''],   // was accountNo — now matches DTO
            bankName: [''],
            payeeReference: [''],   // separate field — was wrongly aliased to reference

            // International banking
            currency: [''],
            intermediaryBankName: [''],   // was intermBankName
            intermediaryAccountNumber: [''],   // was intermAccountNo
            intermediarySwiftBicCode: [''],   // was intermSwift
            intermediaryReference: [''],   // was interReference
            beneficiaryAccountNumber: [''],   // was finalBeneficiaryAccNo
            swiftBicCode: [''],   // was finalBeneficiarySwift
            finalReference: [''],   // reference for final bank

            // Additional info
            otherInformation: [''],   // was otherInfo
            address: [''], 

            // Telephone verification
            isAccountVerified: ['', Validators.required],  // was isAccountDetailsVerifies
            telephoneVerificationDate: [''],
            verificationSpokeTo: [''],
            verificationNoReason: [''],

            // Partner / authorisation
            partnerAuthorisingId: [''],   // was partnerAuthorising (now holds ID directly)
            matterPartner: [false],
            nonPartnerReason: [''],
            fundsCheckedByPartner: [false],
            isPartnerCertified: [false],

            // Files
            uploadedFiles: [[], this.atLeastOneDocument],
        });
    }

    // ── Reactive listeners ─────────────────────────────────────────────────

    private setupFormListeners(): void {
        // Matter selected/cleared
        this.paymentRequestForm.get('matter')!.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(val => this.onMatterValueChange(val));

        // Monies returned toggle
        this.paymentRequestForm.get('isMoniesReturned')!.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(val => this.handleIsMoniesReturned(val));

        // Telephone verification
        this.paymentRequestForm.get('isAccountVerified')!.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(val => this.updateVerificationValidators(val));

        // Matter partner answer
        this.paymentRequestForm.get('matterPartner')!.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(val => this.updateNonPartnerReasonValidator(val));

        // Intermediary bank name (triggers required fields)
        this.paymentRequestForm.get('intermediaryBankName')!.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                if (this.isInternational) this.updateAllDynamicValidators();
            });
    }

    private onMatterValueChange(val: string): void {
        if (!val) {
            this.paymentRequestForm.patchValue({ isMoniesReturned: false, payee: '' });
            this.paymentRequestForm.get('isMoniesReturned')?.disable();
            this.paymentRequestForm.get('payee')?.disable();
        } else {
            this.paymentRequestForm.get('isMoniesReturned')?.enable();
            this.paymentRequestForm.get('payee')?.enable();
        }
    }

    // ── Dynamic validators ─────────────────────────────────────────────────

    updateAllDynamicValidators(): void {
        const isIntl = this.isInternational;
        const isOffice = this.isOfficePayment;
        const isChaps = this.paymentRequestForm.get('chapsRequired')?.value === PAYMENT_METHOD_CHAPS;
        const isPropPurch = this.paymentRequestForm.get('isPropertyPurchase')?.value === true;
        const verified = this.paymentRequestForm.get('isAccountVerified')?.value;

        const allConditional = [
            'chapsRequired', 'isPropertyPurchase', 'purposeCode',
            'sortCode', 'accountNumber', 'bankName', 'payeeReference',
            'currency', 'beneficiaryAccountNumber', 'swiftBicCode', 'finalReference',
            'intermediaryBankName', 'intermediaryAccountNumber',
            'intermediarySwiftBicCode', 'intermediaryReference',
            'vatAmount', 'address'
        ];

        // Clear all conditional validators first
        allConditional.forEach(field => this.clearField(field));

        if (!isIntl) {
            // ── Domestic ──────────────────────────────────────────────────
            this.setRequired('chapsRequired');
            this.setRequired('payeeReference');

            this.paymentRequestForm.get('sortCode')?.setValidators([
                Validators.required,
                Validators.pattern(/^\d{6}$/)
            ]);
            this.paymentRequestForm.get('accountNumber')?.setValidators([
                Validators.required,
                Validators.pattern(/^\d{8}$/)
            ]);
            this.paymentRequestForm.get('sortCode')?.updateValueAndValidity({ emitEvent: false });
            this.paymentRequestForm.get('accountNumber')?.updateValueAndValidity({ emitEvent: false });

            if (isChaps) {
                this.setRequired('isPropertyPurchase');
                if (isPropPurch) this.setRequired('purposeCode');
            }

            // Clear international-only fields
            ['currency', 'intermediaryBankName', 'intermediaryAccountNumber',
                'intermediarySwiftBicCode', 'intermediaryReference',
                'beneficiaryAccountNumber', 'swiftBicCode', 'finalReference'
            ].forEach(f => this.paymentRequestForm.get(f)?.setValue(null, { emitEvent: false }));

        } else {
            // ── International ─────────────────────────────────────────────
            this.setRequired('currency');
            this.setRequired('beneficiaryAccountNumber');
            this.setRequired('swiftBicCode');
            this.setRequired('finalReference');

            const intermBank = this.paymentRequestForm.get('intermediaryBankName')?.value?.trim();
            if (intermBank) {
                this.setRequired('intermediaryBankName');
                this.setRequired('intermediaryAccountNumber');
                this.setRequired('intermediarySwiftBicCode');
                this.setRequired('intermediaryReference');
            }

            // Clear domestic-only fields
            ['chapsRequired', 'isPropertyPurchase', 'purposeCode', 'sortCode', 'accountNumber']
                .forEach(f => this.paymentRequestForm.get(f)?.setValue(null, { emitEvent: false }));
        }

        const isChapsSelected = this.paymentRequestForm.get('chapsRequired')?.value === PAYMENT_METHOD_CHAPS;
        if (isIntl) {
            this.setRequired('address');
        } else if (!isChapsSelected) {
            this.paymentRequestForm.get('address')?.setValue('', { emitEvent: false });
        }

        // VAT: only required for office payments
        const vatCtrl = this.paymentRequestForm.get('vatAmount');
        if (isOffice) {
            vatCtrl?.setValidators([Validators.required, Validators.pattern(/^[0-9.,]*$/)]);
        } else {
            vatCtrl?.setValue(null, { emitEvent: false });
        }
        vatCtrl?.updateValueAndValidity({ emitEvent: false });

        this.updateVerificationValidators(verified);
        this.handleIsMoniesReturned(this.paymentRequestForm.get('isMoniesReturned')?.value);
        this.updatePartnerAuthorisingValidator();
        this.updateNonPartnerReasonValidator(this.paymentRequestForm.get('matterPartner')?.value);

        this.paymentRequestForm.updateValueAndValidity({ emitEvent: false });
    }

    private handleIsMoniesReturned(val: boolean): void {
        const explanationCtrl = this.paymentRequestForm.get('returnMoniesExplanationIfNo');
        const payeeCtrl = this.paymentRequestForm.get('payee');
        const reasonCtrl = this.paymentRequestForm.get('reason');

        reasonCtrl?.setValidators(Validators.required);

        if (val === true) {
            explanationCtrl?.clearValidators();
            explanationCtrl?.setValue('', { emitEvent: false });
            this.isPayeeReadOnly = true;
            if (!this.restoringDraft) {
                this.loadClientNameIntoPayee();
            }
        } else {
            explanationCtrl?.setValidators(Validators.required);
            this.isPayeeReadOnly = false;
            payeeCtrl?.reset();
            this.payeeClients.setValue([]);
        }

        explanationCtrl?.updateValueAndValidity({ emitEvent: false });
        reasonCtrl?.updateValueAndValidity({ emitEvent: false });
    }

    private updateVerificationValidators(value: string | null): void {
        const dateCtrl = this.paymentRequestForm.get('telephoneVerificationDate');
        const spokeCtrl = this.paymentRequestForm.get('verificationSpokeTo');
        const noReasCtrl = this.paymentRequestForm.get('verificationNoReason');

        dateCtrl?.clearValidators();
        spokeCtrl?.clearValidators();
        noReasCtrl?.clearValidators();

        if (value === 'Yes') {
            dateCtrl?.setValidators(Validators.required);
            spokeCtrl?.setValidators(Validators.required);
            noReasCtrl?.setValue('', { emitEvent: false });
        } else if (value === 'No') {
            noReasCtrl?.setValidators(Validators.required);
            dateCtrl?.setValue(null, { emitEvent: false });
            spokeCtrl?.setValue('', { emitEvent: false });
        } else {
            dateCtrl?.setValue(null, { emitEvent: false });
            spokeCtrl?.setValue('', { emitEvent: false });
            noReasCtrl?.setValue('', { emitEvent: false });
        }

        dateCtrl?.updateValueAndValidity({ emitEvent: false });
        spokeCtrl?.updateValueAndValidity({ emitEvent: false });
        noReasCtrl?.updateValueAndValidity({ emitEvent: false });
    }

    private updatePartnerAuthorisingValidator(): void {
        const authCtrl = this.paymentRequestForm.get('partnerAuthorisingId');
        const fundsCtrl = this.paymentRequestForm.get('fundsCheckedByPartner');
        const certifiedCtrl = this.paymentRequestForm.get('isPartnerCertified');

        if (this.isUserPartner) {
            authCtrl?.clearValidators();
            fundsCtrl?.setValidators(Validators.requiredTrue);
            certifiedCtrl?.setValidators(Validators.requiredTrue);
        } else {
            authCtrl?.setValidators(Validators.required);
            fundsCtrl?.clearValidators();
            certifiedCtrl?.clearValidators();
        }

        authCtrl?.updateValueAndValidity({ emitEvent: false });
        fundsCtrl?.updateValueAndValidity({ emitEvent: false });
        certifiedCtrl?.updateValueAndValidity({ emitEvent: false });
    }

    private updateNonPartnerReasonValidator(isMatterPartner: boolean | null): void {
        const ctrl = this.paymentRequestForm.get('nonPartnerReason');
        if (!isMatterPartner && this.isUserPartner) {
            ctrl?.setValidators(Validators.required);
        } else {
            ctrl?.clearValidators();
            ctrl?.setValue('', { emitEvent: false });
        }
        ctrl?.updateValueAndValidity({ emitEvent: false });
    }

    // ── Helpers ────────────────────────────────────────────────────────────

    private setRequired(controlName: string): void {
        const ctrl = this.paymentRequestForm.get(controlName);
        if (!ctrl) return;
        ctrl.setValidators(Validators.required);
        ctrl.updateValueAndValidity({ emitEvent: false });
    }

    private clearField(controlName: string): void {
        const ctrl = this.paymentRequestForm.get(controlName);
        if (!ctrl) return;
        ctrl.clearValidators();
        ctrl.setErrors(null);
        ctrl.markAsUntouched();
        ctrl.markAsPristine();
        ctrl.updateValueAndValidity({ emitEvent: false });
    }

    // ── Data loading ───────────────────────────────────────────────────────

    private async loadInitialData(): Promise<void> {
        try {
            await Promise.all([this.loadUserRole(), this.loadDropdownData()]);
        } catch {
            this.snackbarService.errorSnackBar('Failed to load initial data');
        }
    }

    private async loadUserRole(): Promise<void> {
        const role = await this.manageUserRoleService.getUserPermissionsByRole(this.userService.userDetails?.userRole!);
        this.isUserPartner = role.partnerApprove;

        this.updatePartnerAuthorisingValidator();
        this.updateNonPartnerReasonValidator(this.paymentRequestForm.get('matterPartner')?.value);
    }

    private async loadDropdownData(): Promise<void> {
        const [bankAccounts, purposeCodes, matters, partners, actionstepUrl, actionstepOverviewUrl] = await Promise.all([
            this.bankAccountService.getAllBankAccounts(),
            this.requestService.getPurchaseCodeLookup(),
            this.matterService.getMatterList('', ''),
            this.requestService.getPartnersList(),
            this.requestService.getActionstepUrl(),
            this.requestService.getActionStepOverviewUrl()
        ]);

        this.bankAccounts = bankAccounts;
        this.purposeCodeDropdown = purposeCodes;
        this.matterDropdownData = matters;
        this.partnerList = partners;
        this.filteredBankAccounts = [...bankAccounts];
        this.actionstepBaseUrl = actionstepUrl;
        this.actionstepOverviewBaseUrl = actionstepOverviewUrl;
    }

    get showPayeeSelect(): boolean {
        return this.clientOptions.length > 1
            && this.paymentRequestForm.get('isMoniesReturned')?.value === true
            && this.payeeMatchesClients();
    }

    /**
     * True when the text in the field is empty or is exactly the ticked clients. Text saved before names were shown
     * as "First Last" (or typed by hand) is not, so it is shown as plain text rather than as a blank dropdown.
     */
    private payeeMatchesClients(): boolean {
        const text = this.paymentRequestForm.get('payee')?.value ?? '';
        const ticked = this.payeeClients.value;
        return !text || text === this.clientOptions.filter(name => ticked.includes(name)).join(', ');
    }

    private async loadClientNameIntoPayee(): Promise<void> {
        if (!this.matterNo) return;
        if (!this.clientOptions.length && !await this.loadClientDetails(Number(this.matterNo))) {
            this.paymentRequestForm.patchValue({ payee: 'Error loading client' });
            return;
        }

        if (this.clientOptions.length > 1) {
            this.onPayeeClientsChange();
        } else {
            this.paymentRequestForm.patchValue({ payee: this.clientOptions[0] ?? 'Client name not found' });
        }
    }

    onPayeeClientsChange(): void {
        const ticked = this.payeeClients.value;
        this.paymentRequestForm.patchValue({
            payee: this.clientOptions.filter(name => ticked.includes(name)).join(', ')
        });
    }

    onPayeeSelectOpened(isOpen: boolean): void {
        if (!isOpen) this.paymentRequestForm.get('payee')?.markAsTouched();
    }

    private clearClients(): void {
        this.clientOptions = [];
        this.payeeClients.setValue([]);
    }

    // ── Matter autocomplete ────────────────────────────────────────────────

    private setupMatterAutocomplete(): void {
        this.paymentRequestForm.get('matter')!.valueChanges.pipe(
            debounceTime(300),
            switchMap(query => {
                const isNumeric = !isNaN(Number(query));
                this.isLoading = true;
                this.noData = true;
                return this.matterService.getMatterList(
                    isNumeric ? (query ?? '') : '',
                    isNumeric ? '' : (query ?? '')
                );
            }),
            takeUntil(this.destroy$)
        ).subscribe({
            next: data => {
                this.matterDropdownData = data ?? [];
                this.noData = !data?.length;
                this.isLoading = false;
            },
            error: err => {
                this.noData = true;
                this.isLoading = false;
                if (err.status === 401) this.commonService.apiSecurityError();
            }
        });
    }

    async lookupSelectionChanged(event: MatOptionSelectionChange<string>): Promise<void> {
        if (!event.isUserInput) return;
        const raw = event.source.viewValue;
        if (!raw?.includes(' - ')) return;

        [this.matterNo, this.matterDesc] = raw.split(' - ');

        if (this.matterNo) {
            this.actionstepMatterUrl = `${this.actionstepBaseUrl}?action_id=${this.matterNo}`;
            this.actionstepMatterOverviewUrl = `${this.actionstepOverviewBaseUrl}/${this.matterNo}`;
            this.showLinkToOpenMatter.set(true);
        }

        this.paymentRequestForm.patchValue({
            matter: `${this.matterNo} - ${this.matterDesc}`,
            matterNumber: this.matterNo,
            clientName: ''
        });

        this.clearClients();
        await this.loadClientDetails(Number(this.matterNo));
        if (this.paymentRequestForm.get('isMoniesReturned')?.value === true) {
            this.loadClientNameIntoPayee();
        }
    }

    /**
     * Loads the matter's clients: all of them, joined, into the Client Name field, and as the options to tick from
     * when the payee is chosen from a list.
     * @returns False when the clients could not be loaded.
     */
    private async loadClientDetails(matterNumber: number): Promise<boolean> {
        const clientCtrl = this.paymentRequestForm.get('clientName');
        this.isLoading = true;
        try {
            this.clientOptions = await this.matterService.getMatterClientNames(matterNumber);
            clientCtrl?.setValue(this.clientOptions.length ? this.clientOptions.join(', ') : 'No client found');
            return true;
        } catch {
            this.clearClients();
            clientCtrl?.setValue('No client found');
            return false;
        } finally {
            this.isLoading = false;
        }
    }

    displayMatter(matter: Matter): string {
        return matter ? `${matter.number} - ${matter.description}` : '';
    }

    // ── Toggle handlers ────────────────────────────────────────────────────

    toggleInternational(event: MatSlideToggleChange): void {
        this.isInternational = event.checked;
        if (this.isInternational && !this.currencyDropdown.length) {
            this.requestService.getAllCurrencies().then(curr => this.currencyDropdown = curr);
        }

        this.showPropertyPurchaseCheckbox = false;
        this.showPurposeCodeDropdown = false;
        if (!this.isInternational) {
            this.paymentRequestForm.patchValue({
                chapsRequired: '',
                isPropertyPurchase: false,
                purposeCode: null,
                address: ''
            }, { emitEvent: false });
        }
        this.updateAllDynamicValidators();
        this.filterBankAccounts();
    }

    togglePayment(event: MatSlideToggleChange): void {
        this.isOfficePayment = event.checked;
        this.updateAllDynamicValidators();
        this.filterBankAccounts();
    }

    onPaymentMethodChange(value: string): void {
        this.showPropertyPurchaseCheckbox = value === PAYMENT_METHOD_CHAPS;
        if (!this.showPropertyPurchaseCheckbox) {
            this.paymentRequestForm.patchValue({ isPropertyPurchase: false, purposeCode: null });
            this.showPurposeCodeDropdown = false;
        }
        this.updateAllDynamicValidators();
    }

    onPropertyPurchaseChange(event: MatCheckboxChange): void {
        this.showPurposeCodeDropdown = event.checked;
        if (!event.checked) this.paymentRequestForm.get('purposeCode')?.setValue(null);
        this.updateAllDynamicValidators();
    }

    filterBankAccounts(): void {
        this.filteredBankAccounts = [...this.bankAccounts];
    }

    private toTimeString(date: Date): string {
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }

    // ── Submit ─────────────────────────────────────────────────────────────

    async submit(formDirective: FormGroupDirective): Promise<void> {
        // The Enter key can submit even when the button is disabled; a draft that is still being saved must not be submitted.
        if (this.draftEditor.saving()) return;

        if (this.paymentRequestForm.invalid) {
            this.paymentRequestForm.markAllAsTouched();

            this.snackbarService.errorSnackBar('Please complete all required fields');
            return;
        }

        this.submitBtn = true;
        const f = this.paymentRequestForm.value;

        const datePart: Date = f.dateRequested instanceof Date? f.dateRequested : new Date(f.dateRequested);

        const [hours, minutes] = (f.requestedTime ?? '00:00').split(':').map(Number);

        const requestedDate = new Date(datePart.getFullYear(), datePart.getMonth(), datePart.getDate(), hours, minutes, 0, 0);

        const cleanAmount = f.amount
            ? Number(String(f.amount).replace(/,/g, ''))
            : 0;

        const cleanVat = f.vatAmount && this.isOfficePayment
            ? Number(String(f.vatAmount).replace(/,/g, ''))
            : undefined;

        const selectedPartner = this.partnerList.find(p => p.id === f.partnerAuthorisingId);
        const selectedCurrency = this.currencyDropdown.find(c => c.currencyCode === f.currency);
        const selectedPurchase = this.purposeCodeDropdown.find(p => p.id === f.purposeCode);

        // ── Build DTO — all names now match AddPaymentRequestDto exactly ──
        const dto: AddPaymentRequestDto = {
            draftId: this.draftEditor.draftId() ?? undefined,

            // Core identity
            matterNo: Number(this.matterNo),
            matter: this.matterDesc,
            clientName: f.clientName ?? undefined,
            clientOffice: this.isOfficePayment ? 'Office' : 'Client',
            clientBankAccountName: f.clientBankAccount
             ? `${(f.clientBankAccount as BankAccount).accountId} - ${(f.clientBankAccount as BankAccount).description}`
            : undefined,
            clientBankAccountId: (f.clientBankAccount as BankAccount)?.accountId ?? undefined,
            currencyId: selectedCurrency?.id ?? 0,
            partnerAuthorisingId: selectedPartner?.id,
            purchaseCodeId: selectedPurchase?.id ?? undefined,
            paymentType: this.isInternational ? 'International' : 'Domestic',
            paymentMethod: f.chapsRequired === PAYMENT_METHOD_CHAPS ? 'Chaps' : 'Faster Pay',
            reason: f.reason,
            returnMoniesExplanationIfNo: f.returnMoniesExplanationIfNo ?? '',
            amount: cleanAmount,
            vatAmount: cleanVat,
            payee: f.payee ?? undefined,
            sortCode: f.sortCode || undefined,
            accountNumber: f.accountNumber || undefined,
            bankName: f.bankName || undefined,
            payeeReference: f.payeeReference || undefined,
            transactionReference: undefined,                          // not collected in form
            beneficiaryAccountNumber: f.beneficiaryAccountNumber || undefined,
            swiftBicCode: f.swiftBicCode || undefined,
            intermediaryBankName: f.intermediaryBankName || undefined,
            intermediaryAccountNumber: f.intermediaryAccountNumber || undefined,
            intermediarySwiftBicCode: f.intermediarySwiftBicCode || undefined,
            intermediaryReference: f.intermediaryReference || undefined,

            // Additional info
            otherInformation: f.otherInformation || undefined,    // was otherInfo
            relevantInfo: undefined,                          // not collected in form
            address: f.address || undefined,

            // Partner / authorisation
            moniesReturnedClient: f.isMoniesReturned ?? false,
            matterPartner: f.matterPartner ?? false,
            nonPartnerReason: f.nonPartnerReason || undefined,
            isPartnerCertified: f.isPartnerCertified ?? false,
            fundsCheckedByPartner: f.fundsCheckedByPartner ?? false,

            // Date — dateRequested is the requested date combined with timeRequested
            dateRequested: requestedDate,

            // Flags
            chapsRequired: f.chapsRequired === PAYMENT_METHOD_CHAPS,
            accountTelephoneVerification: f.isAccountVerified === 'Yes',
            telephoneVerificationDate: f.telephoneVerificationDate?.getFullYear?.() > 1
                ? f.telephoneVerificationDate
                : null,
            verificationSpokeTo: f.verificationSpokeTo || undefined,
            verificationNoReason: f.verificationNoReason || undefined,
            isPropertyPurchase: f.isPropertyPurchase ?? false,
        };

        let requestCreated = false;
        try {
            const res = await this.requestService.addPaymentRequest(dto);
            requestCreated = true;

            // The request exists from here on, so a failed upload must not be reported as a failed submit.
            let documentsFailed = false;
            if (f.uploadedFiles?.length > 0) {
                const docData: RequestDocAttachmentResponse = {
                    requestId: res.id!,
                    requestType: RequestType.Payment,
                    attachments: f.uploadedFiles
                };
                try {
                    await this.requestService.addRequestDocument(docData);
                    this.clearUpload();
                    this.snackbarService.successSnackBar('Documents uploaded');
                } catch {
                    documentsFailed = true;
                }
            }

            if (documentsFailed) {
                this.snackbarService.errorSnackBar('Payment request submitted, but documents failed to upload');
            } else {
                this.snackbarService.successSnackBar('Payment request submitted successfully');
            }
            this.resetForm(formDirective);
        } catch {
            this.snackbarService.errorSnackBar('Failed to submit payment request');
        } finally {
            this.submitBtn = false;
            if (requestCreated) {
                await this.draftEditor.afterSubmit();
            }
        }
    }

    // ── Reset ──────────────────────────────────────────────────────────────

    resetForm(formDirective?: FormGroupDirective): void {
        this.showLinkToOpenMatter.set(false);
        this.actionstepMatterUrl = '';
        this.actionstepMatterOverviewUrl = '';
        this.matterNo = '';
        this.matterDesc = '';
        this.displayFile = [];
        this.fileAttachments = [];
        this.showPropertyPurchaseCheckbox = false;
        this.showPurposeCodeDropdown = false;
        this.clearClients();

        if (this.fileInput?.nativeElement) this.fileInput.nativeElement.value = '';

        const resetValues = {
            matter: '',
            matterNumber: '',
            clientName: '',
            dateRequested: new Date(),
            requestedTime: this.toTimeString(new Date()),
            isMoniesReturned: false,
            returnMoniesExplanationIfNo: '',
            reason: '',
            payee: '',
            amount: '',
            vatAmount: '',
            clientBankAccount: '',
            chapsRequired: '',
            isPropertyPurchase: false,
            purposeCode: null,
            sortCode: '',
            accountNumber: '',
            bankName: '',
            payeeReference: '',
            currency: '',
            intermediaryBankName: '',
            intermediaryAccountNumber: '',
            intermediarySwiftBicCode: '',
            intermediaryReference: '',
            beneficiaryAccountNumber: '',
            swiftBicCode: '',
            finalReference: '',
            otherInformation: '',
            address: '',
            isAccountVerified: '',
            telephoneVerificationDate: '',
            verificationSpokeTo: '',
            verificationNoReason: '',
            partnerAuthorisingId: '',
            matterPartner: false,
            nonPartnerReason: '',
            fundsCheckedByPartner: false,
            isPartnerCertified: false,
            uploadedFiles: []
        };

        if (formDirective) {
            formDirective.resetForm(resetValues);
        } else {
            this.paymentRequestForm.reset(resetValues);
        }

        Object.values(this.paymentRequestForm.controls).forEach(ctrl => {
            ctrl.markAsPristine();
            ctrl.markAsUntouched();
            ctrl.setErrors(null);
            ctrl.updateValueAndValidity({ emitEvent: false });
        });

        this.updateAllDynamicValidators();
        this.filterBankAccounts();

        // Ensure pristine state after validators re-run
        setTimeout(() => {
            this.paymentRequestForm.markAsPristine();
            this.paymentRequestForm.markAsUntouched();
            this.paymentRequestForm.updateValueAndValidity({ emitEvent: false });
        }, 0);
    }

    clearSearchFilter(): void {
        this.paymentRequestForm.get('matter')?.reset();
        this.paymentRequestForm.get('matterNumber')?.reset();
        this.clearClients();
        this.paymentRequestForm.get('clientName')?.setValue('');
        this.matterDropdownData = [];
        this.matterNo = '';
        this.matterDesc = '';
        this.showLinkToOpenMatter.set(false);
        this.actionstepMatterUrl = '';
        this.actionstepMatterOverviewUrl = '';
    }

    // ── File handling ──────────────────────────────────────────────────────

    onFilesSelected(input: HTMLInputElement): void {
        if (!input.files?.length) {
            this.paymentRequestForm.get('uploadedFiles')?.setValue([]);
            this.paymentRequestForm.get('uploadedFiles')?.markAsTouched();
            return;
        }

        this.fileAttachments = [];
        this.displayFile = [];

        Array.from(input.files).forEach(file => {
            this.displayFile.push(file.name);
            const reader = new FileReader();
            reader.onload = (e: ProgressEvent<FileReader>) => {
                const result = e.target?.result as string;
                const bytes = result.split('base64,')[1];
                this.fileAttachments.push({ fileName: file.name, attachments: bytes });
                this.paymentRequestForm.get('uploadedFiles')?.setValue([...this.fileAttachments]);
                this.paymentRequestForm.get('uploadedFiles')?.markAsTouched();
            };
            reader.readAsDataURL(file);
        });
    }

    clearUpload(): void {
        this.displayFile = [];
        this.fileAttachments = [];
        this.paymentRequestForm.get('uploadedFiles')?.setValue([]);
        this.paymentRequestForm.get('uploadedFiles')?.markAsUntouched();
        this.paymentRequestForm.get('uploadedFiles')?.markAsPristine();
        if (this.fileInput?.nativeElement) this.fileInput.nativeElement.value = '';
    }

    // ── Number formatting ──────────────────────────────────────────────────

    validateInput(event: KeyboardEvent): void {
        if (['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'].includes(event.key)) return;
        if (!/[0-9.,]/.test(event.key)) event.preventDefault();
    }

    allowOnlyDigits(event: KeyboardEvent): void {
        if (!/^[0-9]$/.test(event.key)) event.preventDefault();
    }

    sanitizeSortCode(event: Event): void {
        const input = event.target as HTMLInputElement;
        const digits = input.value.replace(/\D/g, '').slice(0, 6);
        if (digits !== input.value) {
            input.value = digits;
            this.paymentRequestForm.get('sortCode')?.setValue(digits);
        }
    }

    formatNumber(): void {
        const ctrl = this.paymentRequestForm.get('amount');
        this.formatCurrencyControl(ctrl);
    }

    formatVatNumber(): void {
        const ctrl = this.paymentRequestForm.get('vatAmount');
        this.formatCurrencyControl(ctrl);
    }

    private formatCurrencyControl(ctrl: any): void {
        const raw = ctrl?.value;
        if (!raw || raw === '' || raw === '0') {
            ctrl?.setValue('', { emitEvent: false });
            return;
        }
        const clean = String(raw).replace(/,/g, '');
        const num = Number(clean);

        const formatted = this.commonService.formatNumberWithoutRounding(num);
        ctrl?.setValue(formatted, { emitEvent: false });
    }

    // ── Misc ───────────────────────────────────────────────────────────────

    onUrlCopied(): void {
        this.snackbarService.successSnackBar('URL copied to clipboard');
    }
}
